import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from './ChartLayout';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

interface EmploymentSectionProps {
  location: string;
  geofips?: string | null;
  embedOnly?: boolean;
}

interface EmploymentData {
  year: number;
  total_worker: number;
}

interface PopulationData {
  year: number;
  population: number;
}

interface EmploymentMetrics {
  currentWorkers: number | null;
  latestYear: number | null;
  growthRate: number | null;
  peakWorkers: number | null;
  peakYear: number | null;
  currentPopulation: number | null;
  populationLatestYear: number | null;
  populationGrowthRate: number | null;
  populationAnnualGrowthRate: number | null;
}

const tableMap: Record<string, string> = {
  workplace: 'lodes_employees_workplace',
  sex: 'lodes_employees_workplace_sex',
  race: 'lodes_employees_workplace_race',
  income: 'lodes_employees_workplace_income',
  sector: 'lodes_employees_workplace_sector', // Uses S-codes
};

const tabTitleMap: Record<string, string> = {
  workplace: 'Population & Employment Trends',
  sex: 'Employment by Sex',
  race: 'Employment by Race',
  income: 'Employment by Income',
  sector: 'Employment by Sector',
};

const EmploymentSection: React.FC<EmploymentSectionProps> = ({ location, geofips, embedOnly = false }) => {
  const [activeTab, setActiveTab] = useState<'workplace' | 'sex' | 'race' | 'income' | 'sector'>('workplace');
  const [activeView, setActiveView] = useState<'overview' | 'relevance' | 'equity' | 'methodology'>('overview');
  const [chartType, setChartType] = useState<'line' | 'area' | 'percentage'>('line');
  const [chartData, setChartData] = useState<any>(null);
  const [metrics, setMetrics] = useState<EmploymentMetrics>({
    currentWorkers: null,
    latestYear: null,
    growthRate: null,
    peakWorkers: null,
    peakYear: null,
    currentPopulation: null,
    populationLatestYear: null,
    populationGrowthRate: null,
    populationAnnualGrowthRate: null,
  });
  const [populationSeries, setPopulationSeries] = useState<PopulationData[]>([]);
  const [employmentSeries, setEmploymentSeries] = useState<EmploymentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUsingCountyData, setIsUsingCountyData] = useState(false);
  const [countyName, setCountyName] = useState<string | null>(null);
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  // Fetch data based on active tab
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const tableName = tableMap[activeTab];
        if (!tableName) return;

        // Get geofips if not provided
        let locationGeofips: string | null = geofips ? String(geofips) : null;
        let geographyType: string = 'place';
        
        if (!locationGeofips) {
          const geofipsNum = await supabaseLocationService.getGeofips(location);
          locationGeofips = geofipsNum ? String(geofipsNum) : null;
        }

        if (!locationGeofips) {
          throw new Error(`Could not find geofips for location: ${location}`);
        }

        // LODES data is available at place, county, and state levels
        // For cities, first try to find place-level data, then fall back to county-level
        // First, try to get location info to determine if it's a city
        let queryGeofips = locationGeofips;
        
        try {
          const { data: locationInfo, error: locationError } = await supabaseClient
            .from('population')
            .select('geography_type, county')
            .eq('geofips', parseInt(locationGeofips))
            .limit(1)
            .single();

          if (!locationError && locationInfo) {
            if (locationInfo.geography_type === 'City') {
              // First, try to find place-level data for the city
              // Use the city's geofips directly (it should be 7 digits, but we'll try both formats)
              const cityGeofips7 = String(parseInt(locationGeofips)).padStart(7, '0');
              const cityGeofips6 = cityGeofips7.slice(-6);
              
              // Try querying with place geography_type first
              const { data: placeData, error: placeError } = await supabaseClient
                .from(tableName)
                .select('geofips')
                .eq('geofips', cityGeofips6)
                .eq('geography_type', 'place')
                .limit(1)
                .single();
              
              if (!placeError && placeData) {
                // Place-level data exists!
                queryGeofips = cityGeofips6;
                geographyType = 'place';
                setIsUsingCountyData(false);
                setCountyName(null);
                console.log(`EmploymentSection: Using place-level data for city ${location}: geofips=${queryGeofips}`);
              } else {
                // Fall back to county-level data
                const countyName = locationInfo.county || `${location} County`;
                const { data: countyData, error: countyError } = await supabaseClient
                  .from('population')
                  .select('geofips')
                  .eq('location_name', countyName)
                  .eq('geography_type', 'County')
                  .limit(1)
                  .single();
                
                if (!countyError && countyData && countyData.geofips) {
                  queryGeofips = String(countyData.geofips);
                  geographyType = 'county';
                  setIsUsingCountyData(true);
                  setCountyName(countyName);
                  console.log(`EmploymentSection: Using county-level data for city ${location}: county geofips=${queryGeofips}`);
                } else {
                  setIsUsingCountyData(false);
                  setCountyName(null);
                }
              }
            } else if (locationInfo.geography_type === 'County') {
              geographyType = 'county';
            } else if (locationInfo.geography_type === 'State') {
              geographyType = 'state';
              // State geofips is typically "6" for California
              queryGeofips = '6';
            }
          }
        } catch (err) {
          console.warn('EmploymentSection: Could not determine geography type, defaulting to place:', err);
          // If we can't determine, try with the original geofips as place
        }

        // Build query based on table type
        let query = supabaseClient
          .from(tableName)
          .select('*')
          .eq('geofips', queryGeofips)
          .eq('geography_type', geographyType)
          .order('year', { ascending: true });

        const { data, error: queryError } = await query;
        
        if (queryError) throw queryError;
        if (!data || data.length === 0) {
          setLoading(false);
          return;
        }

        // For tables with additional dimensions, create separate datasets
        if (activeTab === 'sex' || activeTab === 'race' || activeTab === 'income' || activeTab === 'sector') {
          // Group by dimension and year
          const dimensionKey = activeTab === 'sex' ? 'sex' : 
                              activeTab === 'race' ? 'race' : 
                              activeTab === 'income' ? 'income' : 'sector';
          
          // Filter data: sex and race tabs should only show data from 2009 onwards
          const minYear = (activeTab === 'sex' || activeTab === 'race') ? 2009 : 2002;
          const filteredData = data.filter((row: any) => row.year >= minYear);
          
          // For sector tab with S-codes, aggregate NAICS sectors into S-codes
          if (activeTab === 'sector') {
            // Map NAICS sectors to S-codes
            const naicsToSCode: Record<string, string> = {
              'NAICS sector 11 (Agriculture, Forestry, Fishing and Hunting)': 'S001',
              'NAICS sector 21 (Mining, Quarrying, and Oil and Gas Extraction)': 'S001',
              'NAICS sector 23 (Construction)': 'S001',
              'NAICS sector 31-33 (Manufacturing)': 'S001',
              'NAICS sector 22 (Utilities)': 'S002',
              'NAICS sector 42 (Wholesale Trade)': 'S002',
              'NAICS sector 44-45 (Retail Trade)': 'S002',
              'NAICS sector 48-49 (Transportation and Warehousing)': 'S002',
              'NAICS sector 51 (Information)': 'S003',
              'NAICS sector 52 (Finance and Insurance)': 'S004',
              'NAICS sector 53 (Real Estate and Rental and Leasing)': 'S004',
              'NAICS sector 54 (Professional, Scientific, and Technical Services)': 'S005',
              'NAICS sector 55 (Management of Companies and Enterprises)': 'S005',
              'NAICS sector 56 (Administrative and Support and Waste Management and Remediation Services)': 'S005',
              'NAICS sector 61 (Educational Services)': 'S006',
              'NAICS sector 62 (Health Care and Social Assistance)': 'S006',
              'NAICS sector 71 (Arts, Entertainment, and Recreation)': 'S007',
              'NAICS sector 72 (Accommodation and Food Services)': 'S007',
              'NAICS sector 81 (Other Services [except Public Administration])': 'S008',
              'NAICS sector 92 (Public Administration)': 'S008',
            };
            
            const sCodeLabels: Record<string, string> = {
              'S000': 'All sectors (total employment)',
              'S001': 'Goods producing',
              'S002': 'Trade, transportation & utilities',
              'S003': 'Information',
              'S004': 'Financial activities',
              'S005': 'Professional & business services',
              'S006': 'Education & health services',
              'S007': 'Leisure & hospitality',
              'S008': 'Other services',
            };
            
            // Aggregate NAICS sectors into S-codes
            const sCodeGrouped: Record<string, Record<number, number>> = {};
            const allYears = new Set<number>();
            
            filteredData.forEach((row: any) => {
              const naicsSector = row[dimensionKey] || 'Unknown';
              const sCode = naicsToSCode[naicsSector];
              
              if (sCode) {
                const year = row.year;
                const workers = row.total_worker || 0;
                
                if (!sCodeGrouped[sCode]) {
                  sCodeGrouped[sCode] = {};
                }
                sCodeGrouped[sCode][year] = (sCodeGrouped[sCode][year] || 0) + workers;
                allYears.add(year);
              }
            });
            
            // Convert to chart format (excluding S000 total)
            const years = Array.from(allYears).sort((a, b) => a - b);
            const datasets = Object.keys(sCodeGrouped)
              .filter(sCode => sCode !== 'S000') // Exclude S000 total
              .sort()
              .map((sCode, idx) => {
                const color = [
                  'rgba(59, 130, 246, 1)',   // Blue
                  'rgba(16, 185, 129, 1)',   // Green
                  'rgba(245, 158, 11, 1)',    // Yellow
                  'rgba(239, 68, 68, 1)',    // Red
                  'rgba(139, 92, 246, 1)',   // Purple
                  'rgba(236, 72, 153, 1)',   // Pink
                  'rgba(34, 197, 94, 1)',    // Emerald
                  'rgba(251, 146, 60, 1)',   // Orange
                  'rgba(99, 102, 241, 1)',   // Indigo
                ][idx % 9];
                
                return {
                  label: sCodeLabels[sCode] || sCode,
                  data: years.map(year => sCodeGrouped[sCode][year] || 0),
                  borderColor: color,
                  backgroundColor: color.replace('1)', '0.1)'),
                  borderWidth: 2,
                  fill: false,
                  tension: 0.1,
                };
              });

            setChartData({ labels: years.map(y => y.toString()), datasets });
            setLoading(false);
            return;
          }
          
          const grouped: Record<string, Record<number, number>> = {};
          const allYears = new Set<number>();
          
          filteredData.forEach((row: any) => {
            const dimension = row[dimensionKey] || 'Unknown';
            const year = row.year;
            const workers = row.total_worker || 0;
            
            if (!grouped[dimension]) {
              grouped[dimension] = {};
            }
            grouped[dimension][year] = (grouped[dimension][year] || 0) + workers;
            allYears.add(year);
          });

          // Convert to chart format with multiple datasets
          const years = Array.from(allYears).sort((a, b) => a - b);
          const datasets = Object.keys(grouped).map((dimension, idx) => {
            const color = [
              'rgba(59, 130, 246, 1)',   // Blue
              'rgba(16, 185, 129, 1)',   // Green
              'rgba(245, 158, 11, 1)',    // Yellow
              'rgba(239, 68, 68, 1)',    // Red
              'rgba(139, 92, 246, 1)',   // Purple
              'rgba(236, 72, 153, 1)',   // Pink
            ][idx % 6];
            
            return {
              label: dimension,
              data: years.map(year => grouped[dimension][year] || 0),
              borderColor: color,
              backgroundColor: color.replace('1)', '0.1)'),
              borderWidth: 2,
              fill: false,
              tension: 0.1,
            };
          });

          setChartData({ labels: years.map(y => y.toString()), datasets });
        } else {
          // Simple tables (workplace) - combined employment + population
          // No filtering needed for these tabs
          const chartDataPoints: EmploymentData[] = data.map((row: any) => ({
            year: row.year,
            total_worker: row.total_worker || 0,
          }));

          setEmploymentSeries(chartDataPoints);

          const geoTypeForPopulation =
            geographyType === 'place' ? 'City' : geographyType === 'county' ? 'County' : 'State';

          const { data: populationData, error: populationError } = await supabaseClient
            .from('population')
            .select('year, population')
            .eq('geofips', parseInt(queryGeofips))
            .eq('geography_type', geoTypeForPopulation)
            .gte('year', 1990)
            .order('year', { ascending: true });

          if (populationError) {
            console.error('Error fetching population data:', populationError);
          }

          const populationPoints: PopulationData[] = (populationData || []).map((row: any) => ({
            year: row.year,
            population: row.population || 0,
          }));

          setPopulationSeries(populationPoints);

          const allYears = Array.from(
            new Set<number>([
              ...chartDataPoints.map((d) => d.year),
              ...populationPoints.map((d) => d.year),
            ])
          ).sort((a, b) => a - b);

          const employmentByYear = new Map(chartDataPoints.map((d) => [d.year, d.total_worker]));
          const populationByYear = new Map(populationPoints.map((d) => [d.year, d.population]));

          setChartData({
            labels: allYears.map((y) => y.toString()),
            datasets: [
              {
                label: 'Total Workers',
                data: allYears.map((year) => employmentByYear.get(year) ?? null),
                borderColor: 'rgba(59, 130, 246, 1)',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderWidth: 2,
                fill: chartType === 'area',
                tension: 0.1,
                pointRadius: 3,
                pointHoverRadius: 5,
                yAxisID: 'y',
              },
              {
                label: 'Population',
                data: allYears.map((year) => populationByYear.get(year) ?? null),
                borderColor: 'rgba(16, 185, 129, 1)',
                backgroundColor: 'rgba(16, 185, 129, 0.08)',
                borderWidth: 2,
                fill: false,
                tension: 0.1,
                pointRadius: 2,
                pointHoverRadius: 4,
                yAxisID: 'y1',
              },
            ],
          });
        }

        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching employment data:', err);
        setError(err.message || 'Failed to load employment data');
        setLoading(false);
      }
    };

    fetchData();
  }, [location, geofips, activeTab, chartType]);

  // Calculate metrics when chartData changes
  useEffect(() => {
    if (!chartData) return;

    // Handle multi-dataset vs single dataset
    let dataPoints: EmploymentData[] = [];
    
    if (activeTab === 'workplace') {
      dataPoints = employmentSeries;
    } else if (Array.isArray(chartData)) {
      dataPoints = chartData;
    } else if (chartData.datasets && chartData.labels) {
      // For multi-dataset, sum across all dimensions for each year
      const years = chartData.labels.map((y: string) => parseInt(y));
      dataPoints = years.map((year: number) => {
        const yearIdx = years.indexOf(year);
        const total = chartData.datasets.reduce((sum: number, dataset: any) =>
          sum + (dataset.data[yearIdx] || 0), 0
        );
        return { year, total_worker: total };
      });
    }

    if (dataPoints.length === 0) return;

    const latest = dataPoints[dataPoints.length - 1];
    const first = dataPoints[0];
    const peak = dataPoints.reduce((max: any, curr: any) => 
      curr.total_worker > max.total_worker ? curr : max
    );

    const growthRate = first.total_worker > 0
      ? ((latest.total_worker - first.total_worker) / first.total_worker) * 100
      : 0;

    const populationLatest = populationSeries.length > 0 ? populationSeries[populationSeries.length - 1] : null;
    const populationFirst = populationSeries.length > 0 ? populationSeries[0] : null;
    const populationGrowthRate = populationFirst && populationLatest && populationFirst.population > 0
      ? ((populationLatest.population - populationFirst.population) / populationFirst.population) * 100
      : null;
    const populationAnnualGrowthRate = populationFirst && populationLatest && populationFirst.population > 0
      ? ((Math.pow(populationLatest.population / populationFirst.population, 1 / (populationLatest.year - populationFirst.year)) - 1) * 100)
      : null;

    setMetrics({
      currentWorkers: latest.total_worker,
      latestYear: latest.year,
      growthRate: growthRate,
      peakWorkers: peak.total_worker,
      peakYear: peak.year,
      currentPopulation: populationLatest ? populationLatest.population : null,
      populationLatestYear: populationLatest ? populationLatest.year : null,
      populationGrowthRate,
      populationAnnualGrowthRate,
    });
  }, [chartData, activeTab, employmentSeries, populationSeries]);

  // Create chart
  useEffect(() => {
    if (!chartRef.current || !chartData) return;

    // Check if chartData is array (simple) or object (multi-dataset)
    const isMultiDataset = chartData && !Array.isArray(chartData) && chartData.datasets;
    if (!isMultiDataset && (!Array.isArray(chartData) || chartData.length === 0)) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    let chartConfig: any;
    let yAxisConfig: any;
    let tooltipCallbacks: any;
    const titleText = `${tabTitleMap[activeTab] || 'Employment'} - ${location}`;

    if (isMultiDataset && activeTab !== 'workplace') {
      // Multi-dataset chart (sex, race, income, sector)
      const originalData = chartData;
      let processedData = { ...originalData };
      
      if (chartType === 'percentage') {
        // Normalize to percentages (sum to 100% for each year)
        const labels = originalData.labels;
        const datasets = originalData.datasets.map((dataset: any) => {
          const percentages = labels.map((_: any, yearIdx: number) => {
            const yearTotal = originalData.datasets.reduce((sum: number, d: any) => 
              sum + (d.data[yearIdx] || 0), 0
            );
            return yearTotal > 0 ? (dataset.data[yearIdx] || 0) / yearTotal * 100 : 0;
          });
          return {
            ...dataset,
            data: percentages,
          };
        });
        processedData = {
          ...originalData,
          datasets,
        };
        
        yAxisConfig = {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: 'Percentage (%)',
          },
          ticks: {
            callback: (value: any) => `${value}%`,
          },
        };
        
        tooltipCallbacks = {
          label: (context: any) => {
            const value = context.parsed?.y;
            if (value == null || typeof value !== 'number') return '';
            return `${context.dataset.label}: ${value.toFixed(1)}%`;
          },
        };
      } else {
        yAxisConfig = {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Number of Workers',
          },
          ticks: {
            callback: (value: any) => value != null && typeof value === 'number' ? value.toLocaleString() : '',
          },
        };
        
        tooltipCallbacks = {
          label: (context: any) => {
            const value = context.parsed?.y;
            if (value == null || typeof value !== 'number') return '';
            return `${context.dataset.label}: ${value.toLocaleString()}`;
          },
        };
      }

      // Update dataset properties based on chart type
      const datasets = processedData.datasets.map((dataset: any) => {
        const baseConfig = {
          ...dataset,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
        };

        if (chartType === 'area' || chartType === 'percentage') {
          return {
            ...baseConfig,
            fill: true,
            tension: 0.1,
          };
        } else {
          return {
            ...baseConfig,
            fill: false,
            tension: 0.1,
          };
        }
      });

      chartConfig = {
        type: 'line',
        data: {
          ...processedData,
          datasets,
        },
      };
      
      // Update yAxisConfig with stacking
      yAxisConfig = {
        ...yAxisConfig,
        stacked: chartType === 'area' || chartType === 'percentage',
      };
    } else {
      // Workplace chart (employment + population)
      chartConfig = {
        type: 'line',
        data: chartData,
      };

      yAxisConfig = {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Number of Workers',
          },
          ticks: {
            callback: (value: any) => value != null && typeof value === 'number' ? value.toLocaleString() : '',
          },
        },
        y1: {
          beginAtZero: true,
          position: 'right' as const,
          grid: { drawOnChartArea: false },
          title: {
            display: true,
            text: 'Population',
          },
          ticks: {
            callback: (value: any) => value != null && typeof value === 'number' ? value.toLocaleString() : '',
          },
        },
      };

      tooltipCallbacks = {
        label: (context: any) => {
          const value = context.parsed.y;
          if (value === null || value === undefined) return '';
          return `${context.dataset.label}: ${value.toLocaleString()}`;
        },
      };
    }

    chartInstance.current = new Chart(ctx, {
      ...chartConfig,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: standardLegend,
          title: standardTitle(titleText),
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: tooltipCallbacks,
          },
        },
        scales: activeTab === 'workplace' ? {
          ...yAxisConfig,
          x: {
            title: {
              display: true,
              text: 'Year',
            },
          },
        } : {
          y: yAxisConfig,
          x: {
            title: {
              display: true,
              text: 'Year',
            },
            stacked: isMultiDataset && (chartType === 'area' || chartType === 'percentage'),
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [chartData, chartType, activeTab, location]);

  const getTabTitle = () => {
    return tabTitleMap[activeTab] || 'Employment';
  };

  const getOverviewContent = () => {
    if (activeTab === 'workplace') {
      const employmentYears = employmentSeries.map((d) => d.year).filter((y) => Number.isFinite(y));
      const populationYears = populationSeries.map((d) => d.year).filter((y) => Number.isFinite(y));

      const earliest = [
        employmentYears.length ? Math.min(...employmentYears) : null,
        populationYears.length ? Math.min(...populationYears) : null,
      ].filter((v): v is number => v != null);
      const latest = [
        employmentYears.length ? Math.max(...employmentYears) : null,
        populationYears.length ? Math.max(...populationYears) : null,
      ].filter((v): v is number => v != null);

      const startYear = earliest.length ? Math.min(...earliest) : 1990;
      const endYear = latest.length ? Math.max(...latest) : 2022;

      return `This chart shows population and employment trends in ${location} from ${startYear} to ${endYear}, using Census Bureau population estimates and LEHD LODES8 employment data.`;
    }
    if (activeTab === 'sex') {
      const labels = (chartData?.labels as string[]) || [];
      const years = labels.map((l) => parseInt(l, 10)).filter((y) => Number.isFinite(y));
      const earliestYear = years.length ? Math.min(...years) : null;
      const latestYear = years.length ? Math.max(...years) : null;

      const startYear = earliestYear ?? '—';
      const endYear = latestYear ?? '—';

      return `This chart shows the number of male and female employees in ${location} from ${startYear} to ${endYear}, using LEHD LODES8 employment data.`;
    }

    // Determine the start year based on the active tab
    // At this point in control-flow, `activeTab` cannot be 'workplace' or 'sex' (they returned above).
    // So remaining possibilities are 'race' | 'income' | 'sector'.
    const startYear = activeTab === 'race' ? 2009 : 2002;
    
    // Check if we're using county-level data for a city (fallback when place data not available)
    const isCityUsingCountyData = location && !location.includes('County') && !location.includes('California') && isUsingCountyData;
    const dataLevelNote = isCityUsingCountyData 
      ? ` Note: City-level employment data is not available for ${location} in LODES, so this chart displays county-level data for ${countyName || 'the county'}.`
      : '';
    
    const baseText = `This chart displays population and ${getTabTitle().toLowerCase()} trends for ${location} from ${startYear} to 2022, using population estimates from the U.S. Census Bureau and employment data from the LEHD Origin-Destination Employment Statistics (LODES8) program.${dataLevelNote} `;
    
    if (metrics.currentWorkers && metrics.latestYear) {
      const growthBaseYear = startYear;
      const locationLabel = isCityUsingCountyData ? 'the county' : location;
      const populationText = metrics.currentPopulation && metrics.populationLatestYear
        ? ` The population reached ${metrics.currentPopulation.toLocaleString()} in ${metrics.populationLatestYear}${metrics.populationAnnualGrowthRate !== null ? `, growing about ${metrics.populationAnnualGrowthRate.toFixed(2)}% per year on average` : ''}.`
        : '';
      return baseText + `As of ${metrics.latestYear}, ${locationLabel} had approximately ${metrics.currentWorkers.toLocaleString()} workers${metrics.growthRate ? `, representing a ${metrics.growthRate > 0 ? 'growth' : 'decline'} of ${Math.abs(metrics.growthRate).toFixed(1)}% since ${growthBaseYear}` : ''}.${populationText}`;
    }
    return baseText;
  };

  const getSexKeyInsightsContent = () => {
    if (activeTab !== 'sex') return null;
    if (!chartData?.datasets || !Array.isArray(chartData.datasets) || !Array.isArray(chartData.labels)) return null;

    const labels = chartData.labels as string[];
    const years = labels.map((l) => parseInt(l, 10)).filter((y) => Number.isFinite(y));
    if (years.length < 2) return null;

    const earliestYear = Math.min(...years);
    const latestYear = Math.max(...years);
    const latestIdx = years.indexOf(latestYear);
    const earliestIdx = years.indexOf(earliestYear);

    const maleDataset = chartData.datasets.find((d: any) => /male/i.test(String(d.label || '')));
    const femaleDataset = chartData.datasets.find((d: any) => /female/i.test(String(d.label || '')));
    if (!maleDataset || !femaleDataset) return null;

    const maleEarliest = Number(maleDataset.data?.[earliestIdx] ?? 0);
    const maleLatest = Number(maleDataset.data?.[latestIdx] ?? 0);
    const femaleEarliest = Number(femaleDataset.data?.[earliestIdx] ?? 0);
    const femaleLatest = Number(femaleDataset.data?.[latestIdx] ?? 0);

    const malePct = maleEarliest > 0 ? ((maleLatest - maleEarliest) / maleEarliest) * 100 : null;
    const femalePct = femaleEarliest > 0 ? ((femaleLatest - femaleEarliest) / femaleEarliest) * 100 : null;

    const formatPctTrend = (v: number | null) => {
      if (v == null) return '—%';
      if (v >= 0) return `up ${v.toFixed(1)}%`;
      return `down ${Math.abs(v).toFixed(1)}%`;
    };

    const maleWorkerText = maleLatest.toLocaleString();
    const femaleWorkerText = femaleLatest.toLocaleString();

    return (
      <p>
        As of {latestYear}, {location} had {maleWorkerText} male workers and {femaleWorkerText} female workers. Over the full period, male/female employment has grown/declined faster, with male employment up{' '}
        {formatPctTrend(malePct)} and female employment {formatPctTrend(femalePct)} since {earliestYear}.
      </p>
    );
  };

  const getWorkplaceKeyInsightsContent = () => {
    if (activeTab !== 'workplace') return null;

    const employmentYears = employmentSeries.map((d) => d.year).filter((y) => Number.isFinite(y));
    const earliestWorkersYear = employmentYears.length ? Math.min(...employmentYears) : null;
    const latestYear = metrics.latestYear ?? (employmentYears.length ? Math.max(...employmentYears) : null);

    const workers = metrics.currentWorkers;
    const workersGrowthPct = metrics.growthRate;

    const populationLatest = metrics.currentPopulation;
    const populationLatestYear = metrics.populationLatestYear;
    const annualRate = metrics.populationAnnualGrowthRate;

    const workersGrowthText =
      workersGrowthPct != null && earliestWorkersYear != null
        ? `, ${workersGrowthPct >= 0 ? 'up' : 'down'} ${Math.abs(workersGrowthPct).toFixed(1)}% since ${earliestWorkersYear}`
        : '';

    const populationText =
      populationLatest != null && populationLatestYear != null
        ? ` Population reached ${populationLatest.toLocaleString()} by ${populationLatestYear}, growing at roughly ${
            annualRate != null ? `${annualRate.toFixed(2)}%` : '—%'
          } per year on average.`
        : '';

    const workersText =
      workers != null && latestYear != null
        ? `As of ${latestYear}, ${location} had approximately ${workers.toLocaleString()} workers${workersGrowthText}.`
        : `As of latest available year, ${location} shows combined population and employment changes.`;

    return (
      <p>
        {workersText}
        {populationText} Employment and population have not always grown at the same rate. Gaps between them reflect periods of economic expansion or contraction.
      </p>
    );
  };

  const getRelevanceContent = () => {
    if (activeTab === 'workplace') {
      return 'Population and employment trends together tell the story of how a city is growing and whether that growth is translating into local economic opportunity.';
    }
    let tabSpecific = '';
    if (activeTab === 'sex') {
      tabSpecific = ' Employment by sex data highlights gender disparities in the workforce, supporting equity-focused policy development.';
    } else if (activeTab === 'race') {
      tabSpecific = ' Employment by race/ethnicity data reveals disparities in economic opportunity, critical for addressing systemic inequities and ensuring inclusive economic growth.';
    } else if (activeTab === 'income') {
      tabSpecific = ' Employment by income category helps identify wage distribution patterns and economic stratification within the community.';
    } else if (activeTab === 'sector') {
      tabSpecific = ' Employment by sector shows the economic composition of the local workforce, essential for understanding economic diversification and resilience.';
    }
    
    return `Population growth directly affects housing demand, infrastructure needs, transportation usage, and energy consumption. Employment data is crucial for understanding local economic conditions, workforce trends, and planning for sustainable development.${tabSpecific} Together, these trends support climate action planning by identifying workforce needs for green jobs, guiding transportation emissions reduction strategies, and ensuring that growth is aligned with equitable climate and resilience goals.`;
  };

  const getEquityContent = () => {
    if (activeTab === 'workplace') {
      return `Growth in both population and jobs doesn't automatically benefit everyone — who captures that growth matters as much as the numbers themselves.`;
    }
    let tabSpecific = '';
    if (activeTab === 'sex') {
      tabSpecific = ' Gender disparities in employment can be exacerbated or addressed through climate policies. For example, green job training programs should ensure equal access for all genders, and transportation improvements should consider the different commuting patterns of men and women.';
    } else if (activeTab === 'race') {
      tabSpecific = ' Racial and ethnic employment disparities are often linked to environmental justice concerns. Communities of color frequently face both higher unemployment rates and greater exposure to environmental hazards. Climate action must address both issues simultaneously, ensuring that green job opportunities are accessible to all communities and that environmental benefits are distributed equitably.';
    } else if (activeTab === 'income') {
      tabSpecific = ' Income-based employment data reveals economic stratification. Climate policies should prioritize job creation in sectors accessible to workers across all income levels, ensuring that the transition to a green economy does not leave low-income workers behind.';
    }
    
    return `Population change can deepen inequities if growth drives displacement, housing insecurity, or unequal access to services. Employment equity is fundamental to climate justice. Climate action policies must consider their impact on employment opportunities, particularly for historically marginalized communities.${tabSpecific} Tracking both population and employment trends helps ensure growth benefits existing residents and expands opportunity across all groups.`;
  };

  const getMethodologyContent = () => {
    return `Data Sources: Population estimates from the U.S. Census Bureau (ACS 5-year) and employment data from LEHD Origin-Destination Employment Statistics (LODES8). Years Available: Population 1990-present; Employment 2002-2022. Geographic Level: Data is aggregated to place (city), county, and state levels using census block group crosswalks. Methodology: Population values are annual estimates for the selected geography. LODES data is derived from state unemployment insurance records and provides detailed information about where people work (workplace) and where they live (origin). The employment data includes demographic breakdowns by sex, race/ethnicity, income, and economic sector (NAICS codes). Limitations: LODES excludes federal employees, self-employed workers, and some agricultural workers. Population estimates are subject to ACS sampling error.`;
  };

  if (loading) {
    return (
      <ChartLayout
      scrollAnchorId="chart-employment"
      title="Population & Employment Trends"
        chartType="line"
        activeView="overview"
        onViewChange={() => {}}
        chartContent={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading employment data...</div>}
      />
    );
  }

  if (error) {
    return (
      <ChartLayout
      scrollAnchorId="chart-employment"
      title="Population & Employment Trends"
        chartType="line"
        activeView="overview"
        onViewChange={() => {}}
        chartContent={<div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>Error: {error}</div>}
      />
    );
  }

  const chartTitle = isUsingCountyData && countyName 
    ? `Population & Employment Trends (${countyName} County Data)`
    : "Population & Employment Trends";

  const employmentChartContent = (
    <div>
      {activeTab !== 'workplace' && !embedOnly && (
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          marginBottom: '16px',
          justifyContent: 'flex-start',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setChartType('line')}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: chartType === 'line' ? '#3b82f6' : '#ffffff',
              color: chartType === 'line' ? '#ffffff' : '#374151',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: chartType === 'line' ? '600' : '400',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (chartType !== 'line') {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              if (chartType !== 'line') {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }
            }}
          >
            Line
          </button>
          <button
            onClick={() => setChartType('area')}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: chartType === 'area' ? '#3b82f6' : '#ffffff',
              color: chartType === 'area' ? '#ffffff' : '#374151',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: chartType === 'area' ? '600' : '400',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (chartType !== 'area') {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              if (chartType !== 'area') {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }
            }}
          >
            Area (Stacked)
          </button>
          <button
            onClick={() => setChartType('percentage')}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: chartType === 'percentage' ? '#3b82f6' : '#ffffff',
              color: chartType === 'percentage' ? '#ffffff' : '#374151',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: chartType === 'percentage' ? '600' : '400',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (chartType !== 'percentage') {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              if (chartType !== 'percentage') {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }
            }}
          >
            Percentage (Stacked Area 100%)
          </button>
        </div>
      )}
      <div style={{ position: 'relative', height: embedOnly ? '250px' : '400px' }}>
        <canvas ref={chartRef} />
      </div>
    </div>
  );

  if (embedOnly) {
    return employmentChartContent;
  }

  const chartContent = employmentChartContent;

  return (
    <ChartLayout
      scrollAnchorId="chart-employment"
      title={chartTitle}
      chartType="line"
      activeView={activeView}
      onViewChange={(view: string) => setActiveView(view as 'overview' | 'relevance' | 'equity' | 'methodology')}
      keyMetrics={
        <KeyMetrics>
          {metrics.currentWorkers && metrics.latestYear && (
            <KeyMetric
              value={metrics.currentWorkers.toLocaleString()}
              label={`Workers (${metrics.latestYear})`}
              color="#1e3a8a"
            />
          )}
          {metrics.growthRate !== null && (
            <KeyMetric
              value={`${metrics.growthRate > 0 ? '+' : ''}${metrics.growthRate.toFixed(1)}%`}
              label="Growth since 2002"
              color={metrics.growthRate > 0 ? "#10b981" : "#ef4444"}
            />
          )}
          {metrics.peakWorkers && metrics.peakYear && (
            <KeyMetric
              value={metrics.peakWorkers.toLocaleString()}
              label={`Peak (${metrics.peakYear})`}
              color="#f59e0b"
            />
          )}
        </KeyMetrics>
      }
      chartSelector={{
        value: activeTab,
        onChange: (value: string) => setActiveTab(value as any),
        options: [
          { value: 'workplace', label: 'Total' },
          { value: 'sex', label: 'By Sex' },
          { value: 'race', label: 'By Race' },
          { value: 'income', label: 'By Income' },
          { value: 'sector', label: 'By Sector' },
        ],
      }}
      chartContent={chartContent}
      overviewContent={
        <OverviewContent title={getTabTitle()} description={getOverviewContent()}>
          {activeTab === 'workplace' ? getWorkplaceKeyInsightsContent() : activeTab === 'sex' ? getSexKeyInsightsContent() : null}
        </OverviewContent>
      }
      relevanceContent={
        activeTab === 'workplace' ? (
          <RelevanceContent
            title={`Relevance: ${getTabTitle()}`}
            description={getRelevanceContent()}
          >
            <div className="methodology-details-simple">
              <h5>Housing and Infrastructure Demand</h5>
              <p>
                Population growth puts pressure on housing supply, transit, utilities, and public services. When employment grows faster than population, it signals that many workers are commuting in from elsewhere, creating direct implications for traffic, transit investment, and regional housing needs.
              </p>
            </div>
            <div className="methodology-details-simple">
              <h5>Job Center Dynamics</h5>
              <p>
                Understanding whether a place functions as a net importer or exporter of jobs helps identify where transportation connections, workforce housing, and supporting services are most needed.
              </p>
            </div>
          </RelevanceContent>
        ) : activeTab === 'sex' ? (
          <RelevanceContent
            title={`Relevance: ${getTabTitle()}`}
            description="The gender breakdown of employment reflects both the types of industries driving local job growth and broader patterns in labor force participation."
          >
            <div className="methodology-details-simple">
              <h5>Industry and Occupational Mix</h5>
              <p>
                Male and female employment counts tend to shift as a local economy's industry mix changes. Areas with growing healthcare, education, or service sectors typically see faster growth in female employment, while construction or manufacturing-heavy economies tend to drive male employment growth. Tracking these trends reveals how the local economy is evolving.
              </p>
            </div>
            <div className="methodology-details-simple">
              <h5>Labor Force Participation</h5>
              <p>
                Changes in the gap between male and female employment can signal broader shifts in who is entering or leaving the workforce, whether due to economic conditions, caregiving demands, or structural changes in available job types.
              </p>
            </div>
          </RelevanceContent>
        ) : (
          <RelevanceContent title={`Relevance: ${getTabTitle()}`} description={getRelevanceContent()} />
        )
      }
      equityContent={
        activeTab === 'workplace' ? (
          <EquityContent title={`Equity: ${getTabTitle()}`} description={getEquityContent()}>
            <div className="methodology-details-simple">
              <h5>Workforce Access</h5>
              <p>
                A growing job base only improves equity if local residents—especially lower-income households—can access those jobs. When employment growth is concentrated in high-skill sectors, it can widen income gaps and push service workers further from job centers.
              </p>
            </div>
            <div className="methodology-details-simple">
              <h5>Displacement Pressure</h5>
              <p>
                Rapid population and employment growth drives up housing costs, often displacing residents who were there before the growth arrived. Tracking whether population growth reflects new residents or the displacement of existing ones is critical to understanding who the growth is actually serving.
              </p>
            </div>
          </EquityContent>
        ) : activeTab === 'sex' ? (
          <EquityContent
            title={`Equity: ${getTabTitle()}`}
            description="Employment counts by sex alone are not enough to check equity. What's more important is to determine whether men and women are accessing jobs on equal terms."
          >
            <div className="methodology-details-simple">
              <h5>Occupational Segregation and Wages</h5>
              <p>
                Even where female employment is growing, women are often concentrated in lower-wage occupations. A rising female employee count doesn't necessarily mean the wage gap is closing or that women are accessing the same quality of jobs as men.
              </p>
            </div>
            <div className="methodology-details-simple">
              <h5>Caregiving and Structural Barriers</h5>
              <p>
                Gaps in female employment relative to male employment often reflect unequal caregiving burdens rather than differences in willingness to work. Limited access to childcare, inflexible scheduling, and lack of paid leave disproportionately push women out of the workforce or into part-time roles.
              </p>
            </div>
          </EquityContent>
        ) : (
          <EquityContent title={`Equity: ${getTabTitle()}`} description={getEquityContent()} />
        )
      }
      methodologyContent={
        <MethodologyContent 
          title="Methodology" 
          description={getMethodologyContent()}
          dataSources={[
            'LEHD Origin-Destination Employment Statistics (LODES8), U.S. Census Bureau',
            'Years Available: 2002-2022',
            'Geographic Level: Place (city), County, State'
          ]}
          methodology={[
            'Data is aggregated from census block groups to places using 2020 census geography crosswalks',
            'LODES data is derived from state unemployment insurance records',
            'Includes demographic breakdowns by sex, race/ethnicity, income, and economic sector (NAICS codes)',
            'Data excludes federal employees, self-employed workers, and some agricultural workers',
            'Data is subject to disclosure avoidance techniques that may introduce small statistical noise'
          ]}
        />
      }
      showRelevanceEquity={true}
      enableComments={true}
      location={location}
    />
  );
};

export default EmploymentSection;

